import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, API, authQuery, downloadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Repeat2, Upload, Download, Check, X, AlertTriangle, FileText, Plus, Search } from "lucide-react";

const STATUS_KEY = { pending: "transfers.status_pending", approved: "transfers.status_approved", rejected: "transfers.status_rejected" };

const STATUS_COLOR = {
  pending: { bg: "#fdf4e0", border: "#c9a84c", color: "#7a5e1a" },
  approved: { bg: "#e8f3e6", border: "#5fa353", color: "#2c5e21" },
  rejected: { bg: "#fde6e6", border: "#b54b4b", color: "#7a1d1d" },
};

export default function TransfersPage() {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";
  const labels = Object.fromEntries(Object.entries(STATUS_KEY).map(([k, v]) => [k, t(v)]));
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [decisionDialog, setDecisionDialog] = useState(null); // {tr, action: "approve"|"reject"}
  const [uploadDialog, setUploadDialog] = useState(null); // {tr}
  const [requestDialog, setRequestDialog] = useState(false);

  const load = async () => {
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/transfers${params}`);
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const tm = setInterval(load, 30000);
    return () => clearInterval(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isAdmin]);

  const incoming = rows.filter((r) => r.my_role === "home"); // we approve/reject these
  const outgoing = rows.filter((r) => r.my_role === "target"); // we wait for the other lodge

  if (!isAdmin) {
    return (
      <div data-testid="transfers-page">
        <h1 className="page-title">{t("transfers.transfers")}</h1>
        <p className="page-subtitle">{t("transfers.admin_access_required")}</p>
      </div>
    );
  }

  return (
    <div data-testid="transfers-page">
      <div className="page-header" style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="page-title">{t("transfers.member_transfers")}</h1>
          <p className="page-subtitle">
            {t("transfers.cross_lodge_mason_transfers_lodge")}
          </p>
        </div>
        <button
          className="btn-primary-gold flex items-center gap-2"
          onClick={() => setRequestDialog(true)}
          data-testid="transfers-new-request-btn"
        >
          <Plus size={14} /> {t("transfers.new_transfer_request")}
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {[
          { key: "pending", label: t("transfers.pending") },
          { key: "approved", label: t("transfers.approved") },
          { key: "rejected", label: t("transfers.rejected") },
          { key: "all", label: t("transfers.all") },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`pill-tab ${filter === tab.key ? "active" : ""}`}
            onClick={() => setFilter(tab.key)}
            data-testid={`transfers-filter-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <TransferGroup
        title={t("transfers.to_approve_incoming")}
        rows={incoming}
        viewerRole="home"
        en={en}
        labels={labels}
        emptyText={t("transfers.no_incoming_requests_in_this")}
        onUpload={(tr) => setUploadDialog({ tr })}
        onDecide={(tr, action) => setDecisionDialog({ tr, action })}
        onReload={load}
      />

      <div className="gold-divider my-5" />

      <TransferGroup
        title={t("transfers.awaiting_outgoing")}
        rows={outgoing}
        viewerRole="target"
        en={en}
        labels={labels}
        emptyText={t("transfers.no_outgoing_requests_in_this")}
        onReload={load}
      />

      {decisionDialog && (
        <DecisionDialog
          state={decisionDialog}
          en={en}
          onClose={() => setDecisionDialog(null)}
          onDone={async () => {
            setDecisionDialog(null);
            await load();
          }}
        />
      )}

      {uploadDialog && (
        <UploadPdfDialog
          tr={uploadDialog.tr}
          en={en}
          onClose={() => setUploadDialog(null)}
          onDone={async () => {
            setUploadDialog(null);
            await load();
          }}
        />
      )}

      {requestDialog && (
        <RequestTransferDialog
          en={en}
          myLodgeId={user?.lodge_id}
          onClose={() => setRequestDialog(false)}
          onDone={async () => {
            setRequestDialog(false);
            setFilter("pending");
            await load();
          }}
        />
      )}
    </div>
  );
}

function TransferGroup({ title, rows, viewerRole, en, labels, emptyText, onUpload, onDecide, onReload }) {
  return (
    <div>
      <div className="section-heading mb-2">{title}</div>
      {rows.length === 0 ? (
        <div className="gold-card" style={{ padding: "1.4rem", textAlign: "center", color: "#9a7530", fontStyle: "italic" }}>
          {emptyText}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((tr) => (
            <TransferCard
              key={tr.id}
              tr={tr}
              viewerRole={viewerRole}
              en={en}
              labels={labels}
              onUpload={onUpload}
              onDecide={onDecide}
              onReload={onReload}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TransferCard({ tr, viewerRole, en, labels, onUpload, onDecide }) {
  const { t } = useTranslation();
  const palette = STATUS_COLOR[tr.status] || STATUS_COLOR.pending;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(t("transfers.en_gb"), { dateStyle: "medium", timeStyle: "short" }) : "—";

  const downloadPdf = () => {
    const url = `${API}/transfers/${tr.id}/download-pdf${authQuery()}`;
    downloadUrl(url, tr.signed_pdf_filename || `transfer_${tr.id}.pdf`);
  };

  const canApprove = viewerRole === "home" && tr.status === "pending";
  const canUpload = viewerRole === "home" && tr.status === "pending";
  const targetIsViewer = viewerRole === "target";

  return (
    <div className="gold-card" style={{ padding: "1rem 1.1rem", borderColor: palette.border, background: palette.bg }} data-testid={`transfer-row-${tr.id}`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "1.05rem" }}>
            {tr.member_first_name} {tr.member_last_name}
          </div>
          <div className="kv-label" style={{ fontSize: "0.78rem", marginTop: 2 }}>
            {en
              ? <>From <b>{tr.home_lodge_name}</b> → <b>{tr.target_lodge_name}</b></>
              : <>De la <b>{tr.home_lodge_name}</b> → <b>{tr.target_lodge_name}</b></>}
          </div>
          <div className="kv-label" style={{ fontSize: "0.7rem", marginTop: 2 }}>
            {t("transfers.requested")}: {fmtDate(tr.requested_at)}
            {tr.requested_by && <> · {tr.requested_by}</>}
          </div>
          {tr.decided_at && (
            <div className="kv-label" style={{ fontSize: "0.7rem", marginTop: 2 }}>
              {t("transfers.decided")}: {fmtDate(tr.decided_at)} {tr.decided_by && `· ${tr.decided_by}`}
            </div>
          )}
          {tr.decision_reason && (
            <div style={{ marginTop: 6, padding: "6px 8px", background: "rgba(255,255,255,0.6)", border: "1px dashed " + palette.border, fontSize: "0.85rem", color: "#34322d" }}>
              <FileText size={11} style={{ display: "inline", marginRight: 4 }} />
              <i>{tr.decision_reason}</i>
            </div>
          )}
        </div>
        <span
          style={{
            fontFamily: "Cinzel, serif", letterSpacing: "0.18em", fontSize: "0.7rem",
            background: palette.color, color: "#fff", padding: "3px 9px", borderRadius: 0,
            whiteSpace: "nowrap",
          }}
          data-testid={`transfer-status-${tr.id}`}
        >
          {labels[tr.status] || tr.status}
        </span>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
        {tr.has_signed_pdf && (
          <button className="btn-outline-gold flex items-center gap-1" onClick={downloadPdf} data-testid={`transfer-download-${tr.id}`}>
            <Download size={12} /> {t("transfers.download_pdf")}
          </button>
        )}
        {canUpload && (
          <button className="btn-outline-gold flex items-center gap-1" onClick={() => onUpload(tr)} data-testid={`transfer-upload-${tr.id}`}>
            <Upload size={12} /> {tr.has_signed_pdf ? (t("transfers.replace_pdf")) : (t("transfers.upload_signed_pdf"))}
          </button>
        )}
        {canApprove && (
          <>
            <button className="btn-primary-gold flex items-center gap-1" onClick={() => onDecide(tr, "approve")} data-testid={`transfer-approve-${tr.id}`}>
              <Check size={12} /> {t("transfers.approve")}
            </button>
            <button className="btn-danger flex items-center gap-1" onClick={() => onDecide(tr, "reject")} data-testid={`transfer-reject-${tr.id}`}>
              <X size={12} /> {t("transfers.reject")}
            </button>
          </>
        )}
        {targetIsViewer && tr.status === "pending" && !tr.has_signed_pdf && (
          <span className="kv-label flex items-center gap-1" style={{ fontStyle: "italic", color: "#9a7530" }}>
            <AlertTriangle size={11} /> {t("transfers.awaiting_signed_pdf_from_origin")}
          </span>
        )}
        {targetIsViewer && tr.status === "pending" && tr.has_signed_pdf && (
          <span className="kv-label flex items-center gap-1" style={{ fontStyle: "italic", color: "#3c5a3c" }}>
            <Check size={11} /> {t("transfers.awaiting_decision_from_origin_lodge")}
          </span>
        )}
      </div>
    </div>
  );
}

function DecisionDialog({ state, en, onClose, onDone }) {
  const { t } = useTranslation();
  const { tr, action } = state;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (action === "reject" && !reason.trim()) {
      toast.error(t("transfers.please_provide_a_reason_for"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/transfers/${tr.id}/${action}`, { reason: reason.trim() || null });
      toast.success(action === "approve"
        ? (t("transfers.transfer_approved"))
        : (t("transfers.transfer_rejected")));
      await onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gold-card" style={{ maxWidth: 480, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
            {action === "approve"
              ? (t("transfers.approve_transfer"))
              : (t("transfers.reject_transfer"))}
          </h2>
          <button onClick={onClose} className="text-gray-500" data-testid="transfer-decision-close"><X size={18} /></button>
        </div>
        <p style={{ color: "#34322d", marginBottom: 10 }}>
          {tr.member_first_name} {tr.member_last_name} · {tr.home_lodge_name} → {tr.target_lodge_name}
        </p>
        <label className="label-lodge">
          {action === "approve"
            ? (t("transfers.optional_note"))
            : (t("transfers.reason_for_rejection"))}
        </label>
        <textarea
          className="input-lodge" rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={action === "approve"
            ? (t("transfers.optional"))
            : (t("transfers.required_explain_to_the_requesting"))}
          data-testid="transfer-decision-reason"
        />
        <div className="flex gap-2 justify-end pt-3">
          <button type="button" className="btn-outline-gold" onClick={onClose}>{t("transfers.cancel")}</button>
          <button
            className={action === "approve" ? "btn-primary-gold" : "btn-danger"}
            onClick={submit}
            disabled={busy}
            data-testid="transfer-decision-submit"
          >
            {busy ? "..." : (action === "approve" ? (t("transfers.approve")) : (t("transfers.reject")))}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadPdfDialog({ tr, en, onClose, onDone }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/transfers/${tr.id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("transfers.signed_pdf_uploaded"));
      await onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gold-card" style={{ maxWidth: 480, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
            {t("transfers.upload_signed_pdf")}
          </h2>
          <button onClick={onClose} className="text-gray-500" data-testid="transfer-upload-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="transfer-upload-form">
          <p style={{ color: "#34322d" }}>
            {tr.member_first_name} {tr.member_last_name} · {tr.home_lodge_name} → {tr.target_lodge_name}
          </p>
          <div>
            <label className="label-lodge">{t("transfers.pdf_file")}</label>
            <input
              type="file" accept=".pdf,application/pdf" className="input-lodge" required
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="transfer-upload-file"
            />
          </div>
          <p className="kv-label" style={{ fontSize: "0.7rem" }}>
            {t("transfers.lodge_b_will_be_able")}
          </p>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" className="btn-outline-gold" onClick={onClose}>{t("transfers.cancel")}</button>
            <button type="submit" className="btn-primary-gold" disabled={busy} data-testid="transfer-upload-submit">
              {busy ? "..." : (t("transfers.upload"))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RequestTransferDialog({ en, myLodgeId, onClose, onDone }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [requestingId, setRequestingId] = useState(null);
  // Per-member uploaded PDF state: { [memberId]: File }
  const [pendingPdf, setPendingPdf] = useState({});

  // Debounced search.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/global-members/search?q=${encodeURIComponent(q)}`);
        setResults(data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [query]);

  const pickPdf = (memberId, file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error(t("transfers.please_select_a_pdf"));
      return;
    }
    setPendingPdf((p) => ({ ...p, [memberId]: file }));
  };

  // Create the transfer request AND attach the uploaded PDF atomically:
  // create → upload. If the upload fails the request is still created;
  // Lodge A will see it as a pending transfer with no PDF attached yet.
  const requestTransfer = async (member) => {
    const pdf = pendingPdf[member.id];
    if (!pdf) {
      toast.error(t("transfers.upload_the_signed_transfer_request"));
      return;
    }
    setRequestingId(member.id);
    try {
      const { data: tr } = await api.post("/transfers/request", { member_id: member.id });
      const fd = new FormData();
      fd.append("file", pdf);
      await api.post(`/transfers/${tr.id}/upload-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("transfers.requestSentPdf", { name: `${member.first_name} ${member.last_name}` }));
      await onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRequestingId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content gold-card"
        style={{ maxWidth: 620, width: "96vw", padding: "1.5rem", maxHeight: "85vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="transfer-request-dialog"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em", margin: 0 }}>
            {t("transfers.request_new_transfer")}
          </h2>
          <button onClick={onClose} className="text-gray-500" data-testid="transfer-request-close"><X size={20} /></button>
        </div>
        <p className="kv-label" style={{ fontSize: "0.72rem", marginBottom: 10 }}>
          {t("transfers.search_a_mason_from_another")}
        </p>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <Search size={14} color="#9a7530" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input
            className="input-lodge"
            style={{ paddingLeft: 32 }}
            autoFocus
            placeholder={t("transfers.type_at_least_2_letters")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="transfer-request-search"
          />
        </div>
        <div style={{ overflowY: "auto", flex: 1 }} data-testid="transfer-request-results">
          {searching && <p className="kv-label" style={{ fontSize: "0.78rem", color: "#9a7530" }}>{t("transfers.searching")}</p>}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <p className="kv-label" style={{ fontSize: "0.78rem", color: "#9a7530", fontStyle: "italic" }}>
              {t("transfers.no_mason_found")}
            </p>
          )}
          {results.map((m) => {
            const isMine = m.lodge_id === myLodgeId;
            return (
              <div
                key={m.id}
                className="gold-card"
                style={{ padding: "0.7rem 0.85rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}
                data-testid={`transfer-request-result-${m.id}`}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a" }}>
                    {m.first_name} {m.last_name}
                  </div>
                  <div className="kv-label" style={{ fontSize: "0.7rem", marginTop: 2 }}>
                    {m.home_lodge_name} · {m.grade}
                    {m.status && m.status !== "Active" && (
                      <span style={{ marginLeft: 8, color: "#8a2a2a" }}>· {m.status}</span>
                    )}
                  </div>
                </div>
                {isMine ? (
                  <span className="kv-label" style={{ fontStyle: "italic", color: "#9a7530", fontSize: "0.7rem" }}>
                    {t("transfers.already_in_your_lodge")}
                  </span>
                ) : (
                  <div className="flex items-center gap-2" style={{ flexWrap: "wrap" }}>
                    {pendingPdf[m.id] ? (
                      <>
                        <span
                          className="kv-label"
                          style={{ fontSize: "0.7rem", color: "#2c5e21", display: "flex", alignItems: "center", gap: 4 }}
                          data-testid={`transfer-pdf-ready-${m.id}`}
                        >
                          <FileText size={12} /> {pendingPdf[m.id].name.length > 22 ? pendingPdf[m.id].name.slice(0, 20) + "…" : pendingPdf[m.id].name}
                        </span>
                        <button
                          className="btn-primary-gold flex items-center gap-1"
                          onClick={() => requestTransfer(m)}
                          disabled={requestingId === m.id}
                          data-testid={`transfer-request-btn-${m.id}`}
                        >
                          <Repeat2 size={12} /> {requestingId === m.id ? "..." : (t("transfers.request"))}
                        </button>
                      </>
                    ) : (
                      <label
                        className="btn-outline-gold flex items-center gap-1"
                        style={{ cursor: "pointer", background: "transparent", border: "1px solid #c9a84c" }}
                        data-testid={`transfer-upload-btn-${m.id}`}
                      >
                        <Upload size={12} /> {t("transfers.upload_request_pdf")}
                        <input
                          type="file"
                          accept="application/pdf"
                          style={{ display: "none" }}
                          onChange={(e) => pickPdf(m.id, e.target.files?.[0])}
                          data-testid={`transfer-pdf-input-${m.id}`}
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 justify-end pt-3">
          <button type="button" className="btn-outline-gold" onClick={onClose}>{t("transfers.close")}</button>
        </div>
      </div>
    </div>
  );
}

